// src/component-files/forms/ExpenseEntry.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Typography,
    CircularProgress,
    useTheme,
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { tokens } from "../../theme";
import Header from "../../components/Header";

const API_EXPENSES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Expenses";
const API_UPLOAD = "https://media.swancreate.com/upload";
const API_CATEGORIES = "https://figazuresql.swancreate.com/api/swa/finance_categories";
const API_SUBCATEGORIES = "https://figazuresql.swancreate.com/api/swa/finance_subcategories";
const API_SCHOOL_MST = "https://figazuresql.swancreate.com/api/swa/SchoolMst";
const API_VENDORS = "https://figazuresql.swancreate.com/api/swa/FINANCE_vendors";

export default function ExpenseEntry({ onClose, onSaved }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const blank = {
        tempId: uuidv4(),
        siteId: "",
        vendorId: "",
        vendorName: "",
        categoryId: "",
        subcategoryId: "",
        expenseDate: "",
        description: "",
        amount: "",
        file: null,
        uploading: false,
        attachmentUrl: "",
        laborHours: "",          // ← new field
    };

    const [rows, setRows] = useState([{ ...blank }]);
    const [schools, setSchools] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcats, setSubcats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // Fetch lookups
    useEffect(() => {
        Promise.all([
            axios.get(API_SCHOOL_MST),
            axios.get(API_CATEGORIES),
            axios.get(API_SUBCATEGORIES),
            axios.get(API_VENDORS),
        ])
            .then(([sRes, cRes, scRes, vRes]) => {
                setSchools(
                    sRes.data.sort((a, b) => a.SchoolName.localeCompare(b.SchoolName))
                );
                setCategories(cRes.data);
                setSubcats(scRes.data);
                setVendors(vRes.data);
            })
            .catch((err) => {
                console.error(err);
                setError("Lookup load failed");
            })
            .finally(() => setLoading(false));
    }, []);

    const addRow = () =>
        setRows((r) => [...r, { ...blank, tempId: uuidv4() }]);
    const removeRow = (id) =>
        setRows((r) => r.filter((x) => x.tempId !== id));

    const handleChange = (id, field, val) => {
        setRows((r) =>
            r.map((row) => {
                if (row.tempId !== id) return row;
                const updated = { ...row, [field]: val };

                // when vendor changes, also record its name
                if (field === "vendorId") {
                    const sel = vendors.find(v => v.VendorID === val);
                    updated.vendorName = sel ? sel.VendorName : "";
                }

                // when category changes, clear subcategory and laborHours
                if (field === "categoryId") {
                    updated.subcategoryId = "";
                    updated.laborHours = "";   // ← clear labor hours if new category
                }
                return updated;
            })
        );
    };

    // Upload a single file, return full public URL
    const uploadFile = async (file) => {
        const ext = file.name.slice(file.name.lastIndexOf("."));
        const name = `${uuidv4()}${ext}`;
        const fd = new FormData();
        fd.append("file", file, name);
        fd.append("folder", "figfinance");
        await axios.post(API_UPLOAD, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return `https://media.swancreate.com/images/figfinance/${name}`;
    };

    const handleSubmit = async () => {
        setSaving(true);
        try {
            for (let row of rows) {
                let url = "";
                if (row.file) {
                    url = await uploadFile(row.file);
                }
                const selectedCat = categories.find(c => c.CategoryID === row.categoryId);
                const payload = {
                    ExpenseDate: row.expenseDate,
                    Amount: Number(row.amount) || 0,
                    ExpenseCategoryID: row.categoryId,
                    ExpenseCategory: selectedCat?.CategoryName || "",
                    ExpenseSubCategoryID: row.subcategoryId,
                    ExpenseSubcategory:
                        subcats.find((s) => s.SubcategoryID === row.subcategoryId)
                            ?.SubcategoryName || "",
                    Description: row.description,
                    SchoolId: row.siteId,
                    SiteName:
                        schools.find((s) => s.SchoolId === row.siteId)?.SchoolName || "",
                    VendorID: row.vendorId || null,
                    VendorName: row.vendorName || null,
                    ImageURL: url,
                    imageName: url.split("/").pop(),
                    LaborHours: selectedCat?.kpiid
                        ? Number(row.laborHours) || 0
                        : null,   // ← include labor hours only if kpiid
                };
                await axios.post(API_EXPENSES, payload);
            }
            onSaved?.();
            onClose();
        } catch (err) {
            console.error(err);
            setError("Save failed, check console.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box
            p={2}
            sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                borderRadius: 2,
                width: "100%",
                height: "100%",
                overflow: "auto",
            }}
        >
            <Typography variant="h6" mb={2}>
                Enter New Expenses
            </Typography>
            {error && (
                <Typography color="error" mb={2}>
                    {error}
                </Typography>
            )}

            <Table size="small">
                <TableHead>
                    <TableRow sx={{ backgroundColor: theme.palette.background.default }}>
                        {[
                            "Site",
                            "Vendor",
                            "Category",
                            "Subcategory",
                            "Date",
                            "Description",
                            "Amount",
                            "Labor Hours",
                            "Attachment",
                            ""
                        ].map((h) => (
                            <TableCell
                                key={h}
                                sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: "bold",
                                }}
                            >
                                {h}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((row, idx) => {
                        // lookup category and only show labor if kpiid === 1
                        const selCat = categories.find(c => c.CategoryID === row.categoryId);
                        const showLabor = selCat?.kpiid === 1;

                        return (
                            <TableRow
                                key={row.tempId}
                                sx={{
                                    backgroundColor:
                                        idx % 2 === 0
                                            ? theme.palette.action.hover
                                            : "inherit",
                                }}
                            >
                                {/* Site */}
                                <TableCell>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={row.siteId}
                                            onChange={e =>
                                                handleChange(row.tempId, "siteId", e.target.value)
                                            }
                                        >
                                            <MenuItem value="">—</MenuItem>
                                            {schools.map(s => (
                                                <MenuItem key={s.SchoolId} value={s.SchoolId}>
                                                    {s.SchoolName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>

                                {/* Vendor */}
                                <TableCell>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={row.vendorId}
                                            onChange={e =>
                                                handleChange(row.tempId, "vendorId", e.target.value)
                                            }
                                        >
                                            <MenuItem value="">— none —</MenuItem>
                                            {vendors.map(v => (
                                                <MenuItem key={v.VendorID} value={v.VendorID}>
                                                    {v.VendorName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>

                                {/* Category */}
                                <TableCell>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={row.categoryId}
                                            onChange={e =>
                                                handleChange(row.tempId, "categoryId", e.target.value)
                                            }
                                        >
                                            <MenuItem value="">—</MenuItem>
                                            {categories.map(c => (
                                                <MenuItem key={c.CategoryID} value={c.CategoryID}>
                                                    {c.CategoryName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>

                                {/* Subcategory */}
                                <TableCell>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={row.subcategoryId}
                                            onChange={e =>
                                                handleChange(row.tempId, "subcategoryId", e.target.value)
                                            }
                                            disabled={!row.categoryId}
                                        >
                                            <MenuItem value="">—</MenuItem>
                                            {subcats
                                                .filter(sc => sc.CategoryID === row.categoryId)
                                                .map(sc => (
                                                    <MenuItem
                                                        key={sc.SubcategoryID}
                                                        value={sc.SubcategoryID}
                                                    >
                                                        {sc.SubcategoryName}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>

                                {/* Date */}
                                <TableCell>
                                    <TextField
                                        type="date"
                                        size="small"
                                        value={row.expenseDate}
                                        onChange={e =>
                                            handleChange(row.tempId, "expenseDate", e.target.value)
                                        }
                                        fullWidth
                                    />
                                </TableCell>

                                {/* Description */}
                                <TableCell>
                                    <TextField
                                        size="small"
                                        value={row.description}
                                        onChange={e =>
                                            handleChange(row.tempId, "description", e.target.value)
                                        }
                                        fullWidth
                                    />
                                </TableCell>

                                {/* Amount */}
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        inputProps={{ step: "0.01" }}
                                        value={row.amount}
                                        onChange={e =>
                                            handleChange(row.tempId, "amount", e.target.value)
                                        }
                                        fullWidth
                                    />
                                </TableCell>

                                {/* Labor Hours: only render input when kpiid === 1 */}
                                <TableCell>
                                    {showLabor && (
                                        <TextField
                                            size="small"
                                            type="number"
                                            inputProps={{ step: "0.01", min: 0 }}
                                            value={row.laborHours}
                                            onChange={e =>
                                                handleChange(row.tempId, "laborHours", e.target.value)
                                            }
                                            fullWidth
                                            placeholder="0.00"
                                        />
                                    )}
                                </TableCell>

                                {/* Attachment */}
                                <TableCell>
                                    <input
                                        type="file"
                                        style={{ display: "none" }}
                                        id={`file-${row.tempId}`}
                                        onChange={e =>
                                            handleChange(row.tempId, "file", e.target.files[0])
                                        }
                                    />
                                    <label htmlFor={`file-${row.tempId}`}>
                                        <Button
                                            component="span"
                                            size="small"
                                            variant="contained"
                                            disabled={row.uploading}
                                            sx={{
                                                backgroundColor: theme.palette.success.main,
                                                color: theme.palette.common.white,
                                                "&:hover": {
                                                    backgroundColor: theme.palette.success.dark,
                                                },
                                            }}
                                        >
                                            {row.file ? row.file.name : "Choose…"}
                                        </Button>
                                    </label>
                                </TableCell>

                                {/* Remove */}
                                <TableCell>
                                    <IconButton onClick={() => removeRow(row.tempId)}>
                                        <RemoveCircleOutline
                                            sx={{ color: theme.palette.error.main }}
                                        />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        );
                    })}

                    {/* Add Line */}
                    <TableRow>
                        <TableCell colSpan={10} align="center">
                            <IconButton onClick={addRow}>
                                <AddCircleOutline sx={{ color: theme.palette.info.main }} /> Add Line
                            </IconButton>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>


            <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white,
                        "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                        },
                    }}
                >
                    {saving ? <CircularProgress size={20} /> : "Save All"}
                </Button>
            </Box>
        </Box>
    );
}
